import styled, { css } from "styled-components";
import {
  borderRadius,
  borderWidth,
  fontSizes,
  fontWeights,
  lineHeights,
  rawColors,
  spacing,
  typographyMixin,
} from "@magicbi/ui-library";
import type { DistributionCalloutTone, DistributionStepSectionVariant } from "./DistributionStepSection.types";

export const Section = styled.section<{ $variant?: DistributionStepSectionVariant }>`
  display: grid;
  gap: ${spacing[3]};
  min-width: 0;

  ${({ $variant }) =>
    $variant === "card" &&
    css`
      gap: ${spacing[3.5]};
      padding: 1.375rem ${spacing[6]}; /* GAP: 22px vertical padding */
      background: ${({ theme }) => theme.colors.surface.primary};
      border: 1px solid ${({ theme }) => theme.colors.border.subtle};
      border-radius: ${borderRadius.xl};
    `}
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

export const Heading = styled.h2`
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Right side of a card section's heading row. */
export const Aside = styled.div`
  font-size: ${fontSizes["13"]};
  line-height: ${lineHeights["20"]};
  font-weight: ${fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* Shared by the step molecules: helper text, field errors and callouts. */

export const Hint = styled.div`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ErrorText = styled.div`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.error.text};
`;

const TONE = {
  info: css`
    background: ${({ theme }) => theme.colors.background.tertiary};
    & > svg {
      color: ${({ theme }) => theme.colors.text.link};
    }
  `,
  ok: css`
    background: ${({ theme }) => theme.colors.background.secondary};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    & > svg {
      color: ${({ theme }) => theme.colors.success.text};
    }
  `,
  // The amber tint has no icon: the tone is carried by the fill alone.
  warn: css`
    background: ${rawColors.accent.amber50};
    & > svg {
      display: none;
    }
  `,
  error: css`
    background: ${rawColors.accent.red50};
    & > svg {
      color: ${({ theme }) => theme.colors.error.text};
    }
  `,
};

/** A note with an icon: info, ok, warning or error. Borderless tinted banner. */
export const Callout = styled.div<{ $tone: DistributionCalloutTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2.5]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius[10]};
  font-size: ${fontSizes["13"]};
  line-height: ${lineHeights["20"]};
  font-weight: ${fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  ${({ $tone }) => TONE[$tone]}
  & > svg {
    flex: none;
    margin-top: ${spacing[0.5]};
  }
  b {
    font-weight: 600;
  }
`;

/** A text button inside copy ("Replace with {{value}}", "Fix"). */
export const LinkButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  ${typographyMixin("caption", 2)}
  font-size: ${fontSizes["13"]};
  color: ${({ theme }) => theme.colors.text.link};
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    text-decoration: none;
  }
  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
    border-radius: ${borderRadius.xs};
  }
`;
